import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Card } from '../ui/Card'

/**
 * Produsele al căror cod nu mai există în ExpertAccounts.
 *
 * Cazul care a dus la asta: în ERP, codul `VD310` a fost redenumit în `VD3K210` pe 18.08.2026.
 * Sincronizarea de stoc caută după cod, deci rândul din CRM n-a mai fost găsit de nimeni — timp
 * de o lună, până când produsul n-a putut fi găsit într-o cerere de marfă. Nimic nu s-a stricat
 * zgomotos; pur și simplu a încetat să se mai actualizeze.
 *
 * Semnul e în datele pe care le avem deja: după o sincronizare reușită, toate produsele primesc
 * același moment. Cine rămâne în urmă are un cod pe care ERP-ul nu-l mai cunoaște.
 */

interface Produs {
  id: number
  sku: string
  name: string
  stockAsOf: string | null
}

function zi(iso: string | null): string {
  if (!iso) return 'niciodată'
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`
}

export function ProduseDesincronizate() {
  const q = useQuery<{ produse: Produs[] }>({
    queryKey: ['orders', 'produse-desincronizate'],
    queryFn: async () => (await api.get('/orders/produse-desincronizate')).data,
  })
  const produse = q.data?.produse ?? []

  return (
    <Card title="Coduri necunoscute în ExpertAccounts">
      {produse.length === 0 ? (
        <p className="text-sm text-b4l-text-muted">
          Toate produsele active au primit stoc la ultima sincronizare. Dacă un cod e redenumit în
          ExpertAccounts, produsul apare aici.
        </p>
      ) : (
        <>
          <p className="mb-3 text-sm text-b4l-text">
            Produsele de mai jos nu au primit stoc la ultima sincronizare, deși celelalte au primit.
            Probabil codul a fost schimbat în ExpertAccounts. Până la corectare, stocul lor e vechi,
            iar un transfer sau un bon pe acest cod nu găsește articolul în ExpertAccounts.
          </p>
          <ul className="divide-y divide-b4l-border text-sm">
            {produse.map((p) => (
              <li key={p.id} className="flex items-center gap-3 py-2">
                <span className="font-mono text-xs">{p.sku}</span>
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                <span className="text-xs text-b4l-text-muted">ultimul stoc: {zi(p.stockAsOf)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-b4l-text-dim">
            Se repară verificând codul în ExpertAccounts (Articole → „Record changes” arată
            redenumirea) și punându-l la fel în Setări → Produse.
          </p>
        </>
      )}
    </Card>
  )
}
